// "profile lab": riser main hydrant (valley) -> tank (hill) in side view; leak on the riser, tank drain-down, hydrant pressure
import { pipeLatLngs } from './model';
import { innerD } from './aquaNet';
import { distM } from './aquaTrain';

const G = 9.81, BAR = 1e5 / (1000 * G); // m water column per bar
// matches the riser of the demo project "riser valley -> reservoir" (aquaRiser.js)
export const DEMO_PROF = [[0, 120], [396, 135], [799, 165], [1201, 210], [1602, 245], [2003, 280], [2354, 298], [2384, 300]];
export const zAt = (P, s) => { for (let i = 1; i < P.length; i++) if (s <= P[i][0]) { const [a, za] = P[i - 1], [b, zb] = P[i]; return za + ((zb - za) * (s - a)) / (b - a || 1); } return P[P.length - 1][1]; };

// profile of a drawn pipe from the heights of its objects (taken over from the terrain model), start = lower end
export function pipeProfile(project, pipe) {
  const F = project.fittings || [], ll = pipeLatLngs(pipe, F), P = [];
  let s = 0;
  pipe.points.forEach((x, i) => { if (i) s += distM(ll[i - 1], ll[i]); const z = x?.fid && F.find((f) => f.id === x.fid)?.elev?.v; if (Number.isFinite(+z)) P.push([+s.toFixed(1), +z]); });
  if (P.length < 2) return null;
  const L = P[P.length - 1][0];
  return P[0][1] <= P[P.length - 1][1] ? P : P.map(([a, z]) => [+(L - a).toFixed(1), z]).reverse();
}
export const pipeD = (pipe) => innerD(pipe);

const lam = (Re, k, D) => (Re < 2300 ? 64 / Math.max(Re, 1) : 0.25 / Math.log10(k / 1000 / (3.7 * D) + 5.74 / Re ** 0.9) ** 2);
// friction loss (m) for flow Q (m³/s) over length Lf
const hfL = (Q, Lf, D, k) => { if (Lf <= 0 || Q <= 0) return 0; const v = Q / ((Math.PI * D * D) / 4); return (lam((v * D) / 1.3e-6, k, D) * Lf * v * v) / (D * 2 * G); };
const CQ = 0.6 * Math.sqrt(2 * G);
// leak outflow for head H (water surface) through pipe length Lf, Qh = extra flow through the same piece (open hydrant below)
function leakQ(H, zL, Lf, D, k, a, Qh = 0) {
  let Q = 0;
  for (let i = 0; i < 60; i++) Q = 0.5 * Q + 0.5 * CQ * a * Math.sqrt(Math.max(0, H - zL - hfL(Q + Qh, Lf, D, k)));
  return { Q, hgl: H - hfL(Q + Qh, Lf, D, k) };
}

// ① tank open (valley valve closed): tank drains through the leak, then the riser above the leak drains; hydrant = stagnant column
export function runLab(P, o) {
  const L = P[P.length - 1][0], zH = P[0][1], zTop = P[P.length - 1][1], sL = Math.min(L - 1, Math.max(1, o.sL)), zL = zAt(P, sL);
  const D = o.d / 1000, Ap = (Math.PI * D * D) / 4, a = (Math.PI * (o.hole / 1000) ** 2) / 4, At = o.vol / o.depth;
  const prv = (p) => (o.prv > 0 ? Math.min(p, o.prv) : p), qs = (o.qs || 0) / 1000;
  const pts = [];
  let t = 0, lvl = o.level, sw = L, Q0 = null, tTank = null, steady = false;
  for (let n = 0; n < 20000; n++) {
    const H = lvl > 0 ? zTop + lvl : zAt(P, sw), Lf = (lvl > 0 ? L : sw) - sL;
    if (H - zL < 0.01) break;
    const { Q, hgl } = leakQ(H, zL, Lf, D, o.k, a);
    if (Q0 == null) Q0 = Q;
    if (lvl > 0 && Q <= qs) { steady = true; pts.push([t, Math.max(0, (hgl - zH) / BAR), lvl]); break; }
    const p = Math.max(0, (hgl - zH) / BAR);
    if (n % 5 === 0) pts.push([t, p, lvl]);
    const dt = Math.max(1, Math.min(600, (0.002 * (lvl > 0 ? At : Ap * 50)) / Math.max(lvl > 0 ? Q - qs : Q, 1e-6)));
    if (lvl > 0) { lvl -= ((Q - qs) * dt) / At; if (lvl <= 0) { lvl = 0; tTank = t + dt; } } else sw = Math.max(sL, sw - (Q * dt) / Ap);
    t += dt;
  }
  const pEnd = Math.max(0, (zL - zH) / BAR);
  if (!steady) pts.push([t, pEnd, 0]);
  return { steady, qs: o.qs || 0, sL, zL, zH, zTop, L, Q0, tTank, tEnd: t, pEnd, pEndPrv: prv(pEnd), p0: pts[0]?.[1], pts, vPipe: Ap * (L - sL), vTank: o.vol * (o.level / o.depth) };
}

// ③ measured hydrant pressure (tank closed, riser drained to the leak) -> leak height and all places on the profile at that height
export function invLab(P, pBar) {
  const zL = P[0][1] + pBar * BAR, hits = [];
  for (let i = 1; i < P.length; i++) { const [a, za] = P[i - 1], [b, zb] = P[i]; if ((za - zL) * (zb - zL) <= 0 && za !== zb) hits.push(a + ((b - a) * (zL - za)) / (zb - za)); }
  return { zL, hits };
}

// B: tank + spring open (level held), big leak. Hydrant closed -> static p1, hydrant open with Qh -> flowing p2
export function runB(P, o) {
  const L = P[P.length - 1][0], zH = P[0][1], sL = Math.min(L - 1, Math.max(1, o.sL)), zL = zAt(P, sL), D = o.d / 1000, H = P[P.length - 1][1] + o.level, Qh = o.qh / 1000;
  const a = (Math.PI * (o.holeB / 1000) ** 2) / 4, s1 = leakQ(H, zL, L - sL, D, o.k, a), s2 = leakQ(H, zL, L - sL, D, o.k, a, Qh);
  return { sL, zL, Q1: s1.Q, Q2: s2.Q, p1: (s1.hgl - zH) / BAR, p2: (s2.hgl - hfL(Qh, sL, D, o.k) - zH) / BAR, p0: (H - zH) / BAR, p0f: (H - hfL(Qh, L, D, o.k) - zH) / BAR };
}
// predicted flowing pressure if the leak sits at s and explains the measured static pressure
function predB(P, o, H, H1, s) {
  const L = P[P.length - 1][0], zH = P[0][1], D = o.d / 1000, Lu = L - s, zl = zAt(P, s), dh = H - H1, Qh = o.qh / 1000;
  if (H1 - zl <= 0.05) return null;
  let lo = 0, hi = 3;
  for (let i = 0; i < 50; i++) { const m = (lo + hi) / 2; if (hfL(m, Lu, D, o.k) > dh) hi = m; else lo = m; }
  const Q1 = lo, C = Q1 / Math.sqrt(H1 - zl);
  let Q = Q1;
  for (let i = 0; i < 60; i++) Q = 0.5 * Q + 0.5 * C * Math.sqrt(Math.max(0, H - zl - hfL(Q + Qh, Lu, D, o.k)));
  return { p2: (H - hfL(Q + Qh, Lu, D, o.k) - hfL(Qh, s, D, o.k) - zH) / BAR, Q1, dmm: 1000 * Math.sqrt((4 * C) / (Math.PI * CQ)) };
}
// inverse B: measured static + flowing pressure (and hydrant flow) -> leak position(s)
export function invB(P, o, p1m, p2m) {
  const L = P[P.length - 1][0], zH = P[0][1], H = P[P.length - 1][1] + o.level, H1 = zH + p1m * BAR;
  if (H - H1 < 0.05) return { none: true, hits: [] };
  const roots = (pm) => { const r = []; let prev = null; for (let i = 1; i < 800; i++) { const s = (i / 800) * L, q = predB(P, o, H, H1, s); if (q && prev && q.dmm <= o.d && (prev.f - pm) * (q.p2 - pm) <= 0) { const w = (pm - prev.f) / (q.p2 - prev.f || 1); r.push({ s: prev.s + w * (s - prev.s), q: q.Q1, dmm: q.dmm }); } prev = q ? { s, f: q.p2 } : null; } return r; };
  const hits = roots(p2m).map((h) => ({ ...h, z: zAt(P, h.s) })), band = [...roots(p2m - 0.05), ...roots(p2m + 0.05)].map((h) => h.s);
  return { hits, lo: band.length ? Math.min(...band) : null, hi: band.length ? Math.max(...band) : null };
}

// saved own profile -> sorted [s, z] list (null when fewer than 2 valid points)
export const profP = (pr) => { const P = (pr?.pts || []).map(([s, z]) => [+String(s).replace(',', '.'), +String(z).replace(',', '.')]).filter(([s, z]) => Number.isFinite(s) && Number.isFinite(z)).sort((a, b) => a[0] - b[0]); return P.length >= 2 && P[P.length - 1][0] > P[0][0] ? P.map(([s, z]) => [s - P[0][0], z]) : null; };
// default leak place: half way up
export const midS = (P) => invLab(P, (P[P.length - 1][1] - P[0][1]) / 2 / BAR).hits[0] ?? P[P.length - 1][0] / 2;
